import { armTagsApiRef } from '@internal/plugin-azure-resources-common-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SearchIcon from '@material-ui/icons/Search';
import { Pagination } from '@material-ui/lab';

import { applyTagWrites, type TagWrite } from './applyTagWrites';
import type {
  FailingResourceGroup,
  ResourceGroupCandidate,
  ResourceGroupRow,
} from './buildResourceGroupRows';
import { getRecentTagFixes, recordTagFixes } from './recentTagFixes';
import { useResourceGroupResolution } from './useResourceGroupResolution';
import {
  type SubscriptionTags,
  useSubscriptionTagSuggestions,
} from './useSubscriptionTagSuggestions';
import { useTagWriteAccess } from './useTagWriteAccess';
import { useTeamSubscriptions } from './useTeamSubscriptions';

const ROWS_PER_PAGE = 25;
const BULK_FIELD_ID = 'resource-group-tag-bulk-value';
const SCORECARD_REFRESH_NOTE =
  'Tags are applied in Azure immediately. The scorecard re-reads Azure roughly every 3 hours, so the score updates on the next refresh.';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    borderRadius: 12,
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  content: { paddingTop: 0 },
  controls: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  bulkBox: { display: 'flex', gap: theme.spacing(1), alignItems: 'flex-start' },
  suggestions: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  noticeBox: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
  },
  tableContainer: {
    maxHeight: '45vh',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  unresolvedRow: { opacity: 0.6 },
  nameCell: { fontWeight: 500, wordBreak: 'break-all' },
  metaCell: { fontSize: '0.78rem', color: theme.palette.text.secondary },
  valueCell: { minWidth: 220 },
  suggestedTag: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  statusCell: { whiteSpace: 'nowrap', fontSize: '0.78rem' },
  statusFailed: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: theme.palette.error.main,
  },
  successIcon: { color: theme.palette.success.main },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1.5),
  },
  emptyState: { textAlign: 'center', padding: theme.spacing(4) },
}));

/** Fixed already, here or straight in Azure, so it passes once the scorecard refreshes. */
const isAwaitingRefresh = (
  candidate: ResourceGroupCandidate,
  recentFixes: Map<string, number>,
) => candidate.tagged || recentFixes.has(candidate.id.toLowerCase());

export type ResourceGroupTagDialogProps = {
  tagKey: string;
  tagLabel: string;
  checkLabel: string;
  resourceGroups: FailingResourceGroup[];
  teamId?: string;
  onClose: (result: { appliedCount: number }) => void;
};

export const ResourceGroupTagDialog = ({
  tagKey,
  tagLabel,
  checkLabel,
  resourceGroups,
  teamId,
  onClose,
}: ResourceGroupTagDialogProps) => {
  const classes = useStyles();
  const armTagsApi = useApi(armTagsApiRef);
  const {
    teamSubscriptionIds,
    namesById,
    loading: subscriptionsLoading,
  } = useTeamSubscriptions(teamId);

  const {
    loading: resolving,
    error,
    rows,
  } = useResourceGroupResolution(
    resourceGroups,
    tagKey,
    teamSubscriptionIds,
    !subscriptionsLoading,
  );

  // Per-row state is keyed by `row.key`: the same name can sit in several
  // subscriptions.
  const [values, setValues] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  /** Failure message per row; cleared at the start of each run. */
  const [failures, setFailures] = useState<Record<string, string>>({});
  const [appliedKeys, setAppliedKeys] = useState<string[]>([]);
  const [bulkValue, setBulkValue] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [progress, setProgress] = useState<{ done: number; total: number }>();

  const { access } = useTagWriteAccess(rows);

  /** Subscriptions the rows can be written to; each gets its own suggestion. */
  const targetSubscriptionIds = useMemo(
    () =>
      Array.from(
        new Set(
          (rows ?? []).flatMap(row =>
            row.candidates.map(candidate => candidate.subscriptionId),
          ),
        ),
      ),
    [rows],
  );

  const { bySubscription: suggestedTags, loading: suggestionsLoading } =
    useSubscriptionTagSuggestions(targetSubscriptionIds, !!rows);

  // Rows must not appear editable before their permissions are known, so the
  // access map has to cover every candidate currently on screen.
  const accessResolved = useMemo(
    () =>
      !!rows &&
      rows.every(row =>
        row.candidates.every(candidate => candidate.id in access),
      ),
    [rows, access],
  );
  const loading =
    subscriptionsLoading || resolving || !accessResolved || suggestionsLoading;

  const canWrite = useCallback(
    (candidateId?: string) => !!candidateId && access[candidateId] !== 'denied',
    [access],
  );

  useEffect(() => {
    if (!rows) return;

    // Only values already in Azure are seeded; the rest fall back to the
    // subscription's suggestion until the user types or discards it.
    setValues(
      Object.fromEntries(
        rows
          .filter(row => row.target?.existingValue)
          .map(row => [row.key, row.target?.existingValue as string]),
      ),
    );
  }, [rows]);

  useEffect(() => {
    if (!rows) return;
    setSelected(
      Object.fromEntries(rows.map(row => [row.key, canWrite(row.target?.id)])),
    );
  }, [rows, canWrite]);

  const suggestionSubscriptionIds = teamSubscriptionIds.length
    ? teamSubscriptionIds
    : targetSubscriptionIds;

  const { value: suggestions = [] } = useAsync(async () => {
    if (!suggestionSubscriptionIds.length) return [];
    try {
      return await armTagsApi.getTagValueSuggestions(
        suggestionSubscriptionIds,
        tagKey,
      );
    } catch {
      return [];
    }
  }, [armTagsApi, suggestionSubscriptionIds, tagKey]);

  /** The suggestion for the subscription the row is written to, if any. */
  const suggestedValueFor = useCallback(
    (row: ResourceGroupRow) => {
      const subscriptionId = row.target?.subscriptionId;
      if (!subscriptionId) return undefined;
      const suggested =
        suggestedTags[subscriptionId.toLowerCase()]?.[
          tagKey as keyof SubscriptionTags
        ];
      return suggested?.trim() || undefined;
    },
    [suggestedTags, tagKey],
  );

  /** What the row's input shows and what Apply writes. */
  const valueFor = useCallback(
    (row: ResourceGroupRow) => values[row.key] ?? suggestedValueFor(row) ?? '',
    [values, suggestedValueFor],
  );

  /**
   * Rows fixed before this dialog opened. The scorecard still lists them until
   * its next refresh, so they are treated as if tagged here.
   */
  const awaitingRefreshKeys = useMemo(() => {
    const recentFixes = getRecentTagFixes(tagKey);
    return (rows ?? [])
      .filter(
        row =>
          row.candidates.length > 0 &&
          row.candidates.every(candidate =>
            isAwaitingRefresh(candidate, recentFixes),
          ),
      )
      .map(row => row.key);
  }, [rows, tagKey]);

  const doneKeySet = useMemo(
    () => new Set([...appliedKeys, ...awaitingRefreshKeys]),
    [appliedKeys, awaitingRefreshKeys],
  );

  const pendingRows = useMemo(
    () => (rows ?? []).filter(row => !doneKeySet.has(row.key)),
    [rows, doneKeySet],
  );

  const subscriptionLabel = useCallback(
    (subscriptionId: string) => namesById[subscriptionId] ?? subscriptionId,
    [namesById],
  );

  /** The subscription the row belongs to; unknown for an ambiguous bare name. */
  const subscriptionNameFor = useCallback(
    (row: ResourceGroupRow) => {
      const subscriptionId = row.subscriptionId ?? row.target?.subscriptionId;
      if (!subscriptionId) return undefined;
      return row.subscriptionName || subscriptionLabel(subscriptionId);
    },
    [subscriptionLabel],
  );

  const subscriptionTextFor = useCallback(
    (row: ResourceGroupRow) => {
      const name = subscriptionNameFor(row);
      return name && row.target?.location
        ? `${name} · ${row.target.location}`
        : name;
    },
    [subscriptionNameFor],
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pendingRows;
    return pendingRows.filter(row =>
      `${row.name} ${subscriptionTextFor(row) ?? ''}`
        .toLowerCase()
        .includes(term),
    );
  }, [pendingRows, search, subscriptionTextFor]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / ROWS_PER_PAGE),
  );
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  const readyRows = useMemo(
    () =>
      pendingRows.filter(
        row =>
          selected[row.key] && canWrite(row.target?.id) && valueFor(row).trim(),
      ),
    [pendingRows, selected, valueFor, canWrite],
  );
  const suggestedReadyCount = readyRows.filter(
    row => values[row.key] === undefined,
  ).length;
  const selectedCount = pendingRows.filter(row => selected[row.key]).length;
  const bulkTargets = useMemo(
    () => filteredRows.filter(row => selected[row.key]),
    [filteredRows, selected],
  );
  const failedKeys = useMemo(
    () => pendingRows.filter(row => failures[row.key]).map(row => row.key),
    [pendingRows, failures],
  );
  const unresolvedCount = pendingRows.filter(
    row => row.candidates.length === 0,
  ).length;
  const ambiguousCount = pendingRows.filter(row => row.ambiguous).length;
  const readOnlyCount = pendingRows.filter(
    row => row.target && !canWrite(row.target.id),
  ).length;

  const applying = !!progress;

  const runWrites = useCallback(
    async (rowsToWrite: ResourceGroupRow[]) => {
      const writes: TagWrite[] = rowsToWrite.flatMap(row =>
        row.target && canWrite(row.target.id)
          ? [
              {
                key: row.key,
                resourceGroupId: row.target.id,
                tags: { [tagKey]: valueFor(row).trim() },
              },
            ]
          : [],
      );

      setFailures({});
      setProgress({ done: 0, total: writes.length });

      const results = await applyTagWrites(
        writes,
        (resourceGroupId, tags) => armTagsApi.mergeTags(resourceGroupId, tags),
        {
          onResult: () =>
            setProgress(current =>
              current ? { ...current, done: current.done + 1 } : current,
            ),
        },
      );

      const applied = results
        .filter(result => result.status === 'applied')
        .map(result => result.key);
      const failed = results.filter(result => result.status === 'failed');

      const appliedSet = new Set(applied);
      recordTagFixes(
        tagKey,
        writes
          .filter(write => appliedSet.has(write.key))
          .map(write => write.resourceGroupId),
      );
      setAppliedKeys(current => [...current, ...applied]);
      setFailures(
        Object.fromEntries(
          failed.map(result => [result.key, result.message ?? 'Failed']),
        ),
      );
      setProgress(undefined);
    },
    [armTagsApi, tagKey, valueFor, canWrite],
  );

  const handleApply = () => runWrites(readyRows);

  const handleRetryFailed = () =>
    runWrites(pendingRows.filter(row => failedKeys.includes(row.key)));

  const handleBulkApply = () => {
    const value = bulkValue.trim();
    if (!value) return;
    setValues(current => {
      const next = { ...current };
      for (const row of bulkTargets) next[row.key] = value;
      return next;
    });
  };

  const toggleRow = (key: string) =>
    setSelected(current => ({ ...current, [key]: !current[key] }));

  const togglePage = (checked: boolean) =>
    setSelected(current => {
      const next = { ...current };
      for (const row of pageRows) {
        if (canWrite(row.target?.id)) next[row.key] = checked;
      }
      return next;
    });

  const datalistId = `rg-tag-values-${tagKey}`;
  const topSuggestions = suggestions.slice(0, 4);
  const selectablePageRows = pageRows.filter(row => canWrite(row.target?.id));
  const allPageRowsSelected =
    selectablePageRows.length > 0 &&
    selectablePageRows.every(row => selected[row.key]);

  let emptyMessage = 'Nothing to tag here.';
  if (appliedKeys.length > 0) {
    emptyMessage = `Tagged ${appliedKeys.length} resource group${
      appliedKeys.length === 1 ? '' : 's'
    }.`;
  } else if (awaitingRefreshKeys.length > 0) {
    emptyMessage = `${
      awaitingRefreshKeys.length === 1
        ? 'This resource group already carries'
        : `All ${awaitingRefreshKeys.length} resource groups already carry`
    } the ${tagKey} tag in Azure. The scorecard updates on its next refresh.`;
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      onClose={() => !applying && onClose({ appliedCount: appliedKeys.length })}
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6">Fix: {checkLabel}</Typography>
        <Typography variant="caption" color="textSecondary">
          {pendingRows.length} resource group
          {pendingRows.length === 1 ? '' : 's'} failing this check
          {doneKeySet.size > 0 &&
            ` · ${doneKeySet.size} tagged, waiting for scorecard refresh`}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <datalist id={datalistId}>
          {suggestions.map(suggestion => (
            <option key={suggestion.value} value={suggestion.value} />
          ))}
        </datalist>

        <Box className={classes.noticeBox}>
          <InfoOutlinedIcon fontSize="small" color="action" />
          <Typography variant="body2" style={{ fontSize: '0.82rem' }}>
            Sets the <strong>{tagKey}</strong> tag on each resource group in
            Azure, leaving its other tags untouched. {SCORECARD_REFRESH_NOTE}
          </Typography>
        </Box>

        {loading && (
          <Box className={classes.emptyState}>
            <CircularProgress size={28} />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 12 }}
            >
              Resolving resource groups, checking your Azure permissions and
              looking up suggested tag values…
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Box className={classes.noticeBox}>
            <ErrorOutlineIcon fontSize="small" color="error" />
            <Typography variant="body2">
              Could not read resource groups from Azure: {error.message}
            </Typography>
          </Box>
        )}

        {!loading && !error && (
          <>
            <Box className={classes.controls}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search resource groups…"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                style={{ flex: 1, minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ 'aria-label': 'Search resource groups' }}
              />
              <Box>
                <Box className={classes.bulkBox}>
                  <TextField
                    id={BULK_FIELD_ID}
                    size="small"
                    variant="outlined"
                    label="Set all selected to"
                    value={bulkValue}
                    onChange={event => setBulkValue(event.target.value)}
                    style={{ minWidth: 200 }}
                    inputProps={{ list: datalistId }}
                  />
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleBulkApply}
                    disabled={
                      !bulkValue.trim() || applying || bulkTargets.length === 0
                    }
                  >
                    Apply to {bulkTargets.length}
                  </Button>
                </Box>
                {topSuggestions.length > 0 && (
                  <Typography variant="caption" className={classes.suggestions}>
                    In use:{' '}
                    {topSuggestions
                      .map(item => `${item.value} (${item.occurrences})`)
                      .join(' · ')}
                  </Typography>
                )}
              </Box>
            </Box>

            {(unresolvedCount > 0 ||
              ambiguousCount > 0 ||
              readOnlyCount > 0) && (
              <Box className={classes.noticeBox}>
                <InfoOutlinedIcon fontSize="small" color="action" />
                <Typography variant="body2" style={{ fontSize: '0.82rem' }}>
                  {unresolvedCount > 0 &&
                    `${unresolvedCount} of these are not visible with your Azure
                    access — most likely in a subscription you cannot read, or
                    deleted since the last scorecard refresh. `}
                  {ambiguousCount > 0 &&
                    `${ambiguousCount} exist in several subscriptions and the
                    scorecard did not say which one is failing. `}
                  {readOnlyCount > 0 &&
                    `${readOnlyCount} sit in Azure where you have read access but
                    no permission to change tags. `}
                  They stay listed because the check still counts them, but they
                  cannot be tagged from here.
                </Typography>
              </Box>
            )}

            {pendingRows.length === 0 ? (
              <Box className={classes.emptyState}>
                <CheckCircleOutlineIcon className={classes.successIcon} />
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {emptyMessage}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer className={classes.tableContainer}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allPageRowsSelected}
                            onChange={event => togglePage(event.target.checked)}
                            disabled={applying || !selectablePageRows.length}
                            inputProps={{ 'aria-label': 'Select page' }}
                          />
                        </TableCell>
                        <TableCell>Resource group</TableCell>
                        <TableCell>Subscription</TableCell>
                        <TableCell>{tagLabel} value</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map(row => {
                        const failure = failures[row.key];
                        const writable = canWrite(row.target?.id);
                        const suggested =
                          values[row.key] === undefined
                            ? suggestedValueFor(row)
                            : undefined;

                        let blocked = {
                          label: 'No permission to tag',
                          reason: `You need a role that grants ${tagKey} tag writes (for example Tag Contributor or Contributor) on this resource group in Azure.`,
                        };
                        if (row.candidates.length === 0) {
                          blocked = {
                            label: 'Not visible to you',
                            reason:
                              'Not returned by Azure for your account — either you have no read access to its subscription, or it no longer exists.',
                          };
                        } else if (row.ambiguous) {
                          blocked = {
                            label: 'Subscription unknown',
                            reason: `This name exists in ${row.candidates.length} subscriptions and the scorecard did not say which one is failing, so it cannot be tagged from here.`,
                          };
                        }

                        return (
                          <TableRow
                            key={row.key}
                            className={
                              writable ? undefined : classes.unresolvedRow
                            }
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={!!selected[row.key]}
                                onChange={() => toggleRow(row.key)}
                                disabled={applying || !writable}
                                inputProps={{
                                  'aria-label': `Select ${row.name}`,
                                }}
                              />
                            </TableCell>
                            <TableCell className={classes.nameCell}>
                              {row.name}
                              {row.managed && (
                                <Tooltip
                                  arrow
                                  title="Azure creates and manages this resource group. Tags on it are often rejected or reset by the platform."
                                >
                                  <Typography
                                    component="span"
                                    className={classes.metaCell}
                                    style={{ marginLeft: 6 }}
                                  >
                                    Azure-managed
                                  </Typography>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell className={classes.metaCell}>
                              {subscriptionTextFor(row) ?? '—'}
                            </TableCell>
                            <TableCell className={classes.valueCell}>
                              {writable ? (
                                <TextField
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  placeholder={tagKey}
                                  value={valueFor(row)}
                                  onChange={event =>
                                    setValues(current => ({
                                      ...current,
                                      [row.key]: event.target.value,
                                    }))
                                  }
                                  disabled={applying}
                                  inputProps={{
                                    list: datalistId,
                                    'aria-label': `${tagLabel} value for ${row.name}`,
                                  }}
                                  InputProps={
                                    suggested
                                      ? {
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <Tooltip
                                                arrow
                                                title={`Suggested for ${subscriptionNameFor(
                                                  row,
                                                )} from its application tags. Type to replace it, or discard it to skip this resource group.`}
                                              >
                                                <Typography
                                                  component="span"
                                                  className={
                                                    classes.suggestedTag
                                                  }
                                                >
                                                  Suggested
                                                </Typography>
                                              </Tooltip>
                                              <IconButton
                                                size="small"
                                                disabled={applying}
                                                aria-label={`Discard suggested ${tagLabel} for ${row.name}`}
                                                onClick={() =>
                                                  setValues(current => ({
                                                    ...current,
                                                    [row.key]: '',
                                                  }))
                                                }
                                              >
                                                <CloseIcon
                                                  style={{
                                                    fontSize: '0.95rem',
                                                  }}
                                                />
                                              </IconButton>
                                            </InputAdornment>
                                          ),
                                        }
                                      : undefined
                                  }
                                />
                              ) : (
                                <Tooltip arrow title={blocked.reason}>
                                  <span>{blocked.label}</span>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell className={classes.statusCell}>
                              {failure && (
                                <Tooltip arrow title={failure}>
                                  <span className={classes.statusFailed}>
                                    <ErrorOutlineIcon
                                      style={{ fontSize: '0.95rem' }}
                                    />
                                    Failed
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box className={classes.pagination}>
                    <Pagination
                      size="small"
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, nextPage) => setPage(nextPage)}
                      disabled={applying}
                    />
                  </Box>
                )}
              </>
            )}

            {applying && progress && (
              <Box style={{ marginTop: 16 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    progress.total ? (progress.done / progress.total) * 100 : 0
                  }
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 4, display: 'block' }}
                >
                  Applying {progress.done} of {progress.total}…
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Box className={classes.footer}>
          <Typography variant="caption" color="textSecondary">
            {failedKeys.length > 0
              ? `${failedKeys.length} failed · ${readyRows.length} ready to apply`
              : `${readyRows.length} of ${selectedCount} selected have a value${
                  suggestedReadyCount > 0
                    ? ` (${suggestedReadyCount} suggested)`
                    : ''
                }`}
          </Typography>
          <Box style={{ display: 'flex', gap: 8 }}>
            {failedKeys.length > 0 && (
              <Button onClick={handleRetryFailed} disabled={applying}>
                Retry failed
              </Button>
            )}
            <Button
              onClick={() => onClose({ appliedCount: appliedKeys.length })}
              disabled={applying}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
              disabled={applying || readyRows.length === 0}
            >
              Apply tags
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
